import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import {
  GREEN,
  RED,
  colorForSdp,
  nameForSdp,
  type AttackPattern,
  type AttackScenario,
} from "./model.js";

interface SideBySideCardProps {
  readonly side: "binary" | "graded";
  readonly heading: string;
  readonly verdict: string;
  readonly verdictBy?: string;
  readonly body: string;
  readonly expectedGrade?: string;
  readonly presentedGrade?: string;
}

function SideBySideCard({
  side,
  heading,
  verdict,
  verdictBy,
  body,
  expectedGrade,
  presentedGrade,
}: SideBySideCardProps): React.JSX.Element {
  const color = side === "binary" ? RED : GREEN;
  return (
    <div
      className="flex min-h-0 flex-col gap-3 rounded-[14px] border border-solid px-[18px] py-4"
      style={{
        borderColor: `${color}55`,
        background: `linear-gradient(180deg, ${color}10, rgba(5,8,16,0.55))`,
      }}
    >
      <div>
        <div
          className="font-mono text-[10.5px] font-bold uppercase tracking-[0.1em]"
          style={{ color }}
        >
          {side === "binary"
            ? "Binary view (today)"
            : "Graded view (this manuscript)"}
        </div>
        <div className="mt-1 text-[15px] font-extrabold leading-[1.3] text-[var(--tcc-bright)]">
          {heading}
        </div>
      </div>
      <div
        role="status"
        aria-live="polite"
        className="flex flex-wrap items-center gap-2.5 rounded-[10px] border border-solid px-3 py-2.5"
        style={{
          borderColor: `${color}66`,
          background: `${color}1a`,
        }}
      >
        <span
          aria-hidden
          className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-md text-[13px] font-extrabold text-[#050810]"
          style={{ background: color }}
        >
          {side === "binary" ? "✓" : "✕"}
        </span>
        <span
          className="font-mono text-[12.5px] font-bold tracking-[0.08em]"
          style={{ color }}
        >
          {side === "binary"
            ? `verdict: ${verdict}`
            : `verdict: ${verdict}${verdictBy ? ` · by ${verdictBy}` : ""}`}
        </span>
      </div>
      <div className="text-[13px] leading-[1.6] text-[var(--tcc-text)]">
        {body}
      </div>
      {expectedGrade && presentedGrade && (
        <div className="mt-1 flex flex-col gap-1.5 rounded-[10px] border border-[var(--tcc-border)] bg-[rgba(5,8,16,0.6)] px-3 py-2.5 text-[12.5px]">
          <div>
            <span className="mr-1.5 text-[var(--tcc-muted)]">Expected:</span>
            <PanelSurface.InlineCode color={GREEN}>{expectedGrade}</PanelSurface.InlineCode>
          </div>
          <div>
            <span className="mr-1.5 text-[var(--tcc-muted)]">Presented:</span>
            <PanelSurface.InlineCode color={side === "binary" ? panelTokens.muted : RED}>
              {presentedGrade}
            </PanelSurface.InlineCode>
          </div>
        </div>
      )}
    </div>
  );
}

export const PatternDetail = React.memo(function PatternDetail({
  pattern,
}: {
  readonly pattern: AttackPattern;
}): React.JSX.Element {
  const sdpColor = colorForSdp(pattern.sdp);
  return (
    <PanelSurface.Card glowColor={`${sdpColor}55`}>
      <div className="flex flex-wrap items-baseline gap-3.5">
        <span
          className="font-mono text-[22px] font-extrabold"
          style={{
            color: sdpColor,
            textShadow: `0 0 16px ${sdpColor}66`,
          }}
        >
          {pattern.id}
        </span>
        <span className="text-[18px] font-bold tracking-[-0.005em] text-[var(--tcc-bright)]">
          {pattern.title}
        </span>
      </div>
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        {PanelSurface.badge(`generator · ${pattern.generator}`, "#a78bfa")}
        {PanelSurface.badge(`seam · ${pattern.seam}`, "#22d3ee")}
        {PanelSurface.badge(`sdp · ${pattern.sdp} (${nameForSdp(pattern.sdp)})`, sdpColor)}
      </div>

      <div className="mt-4 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3.5">
        <div
          className="rounded-xl border border-solid px-3.5 py-3"
          style={{
            borderColor: `${RED}55`,
            background: `linear-gradient(180deg, ${RED}12, rgba(5,8,16,0.55))`,
          }}
        >
          <div
            className="mb-1.5 font-mono text-[10.5px] font-bold uppercase tracking-[0.1em]"
            style={{ color: RED }}
          >
            Forbidden phantom · binary view admits
          </div>
          <div className="text-[13px] leading-[1.55] text-[var(--tcc-text)]">
            {pattern.forbiddenPhantom}
          </div>
        </div>
        <div
          className="rounded-xl border border-solid px-3.5 py-3"
          style={{
            borderColor: `${GREEN}55`,
            background: `linear-gradient(180deg, ${GREEN}12, rgba(5,8,16,0.55))`,
          }}
        >
          <div
            className="mb-1.5 font-mono text-[10.5px] font-bold uppercase tracking-[0.1em]"
            style={{ color: GREEN }}
          >
            Graded refusal · the position is uninhabited
          </div>
          <div className="text-[13px] leading-[1.55] text-[var(--tcc-text)]">
            {pattern.gradedRefusal}
          </div>
        </div>
      </div>

      <div className="mt-3.5 flex flex-wrap items-center gap-1.5">
        <span className="mr-1 font-mono text-[11px] tracking-[0.06em] text-[var(--tcc-muted)]">
          REFUSED BY ·
        </span>
        {pattern.refusedBy.map((theorem) => PanelSurface.badge(theorem, panelTokens.accentRed))}
      </div>
    </PanelSurface.Card>
  );
});

export const ScenarioDetail = React.memo(function ScenarioDetail({
  scenario,
}: {
  readonly scenario: AttackScenario;
}): React.JSX.Element {
  const sdpColor = colorForSdp(scenario.sdp, panelTokens.accentCyan);
  return (
    <PanelSurface.Card glowColor={`${sdpColor}55`}>
      <div className="flex flex-wrap items-baseline gap-3">
        <span className="text-[19px] font-extrabold tracking-[-0.01em] text-[var(--tcc-bright)]">
          {scenario.title}
        </span>
        {PanelSurface.badge(`pattern · ${scenario.patternId}`, "#a78bfa")}
        {PanelSurface.badge(`sdp · ${scenario.sdp} (${nameForSdp(scenario.sdp)})`, sdpColor)}
      </div>

      <p className="mt-3 text-[14px] leading-[1.6] text-[var(--tcc-text)]">
        {scenario.story}
      </p>

      <div className="mt-3.5 rounded-[10px] border border-[var(--tcc-border)] bg-[rgba(5,8,16,0.45)] px-3 py-2.5">
        <div className="mb-1.5 font-mono text-[10.5px] font-bold uppercase tracking-[0.1em] text-[var(--tcc-muted)]">
          The adversary
        </div>
        <div className="text-[13px] leading-[1.6] text-[var(--tcc-text)]">
          {scenario.adversary}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3.5">
        <SideBySideCard
          side="binary"
          heading="What today's stack sees"
          verdict="ADMITTED"
          body={scenario.binaryView}
          expectedGrade={scenario.expectedGrade}
          presentedGrade={scenario.presentedGrade}
        />
        <SideBySideCard
          side="graded"
          heading="What the type-checker sees"
          verdict="REFUSED"
          verdictBy={scenario.verdictBy}
          body={scenario.gradedRefusal}
          expectedGrade={scenario.expectedGrade}
          presentedGrade={scenario.presentedGrade}
        />
      </div>

      <div
        className="mt-4 rounded-xl border border-solid px-3.5 py-3"
        style={{
          borderColor: `${sdpColor}55`,
          background: `linear-gradient(135deg, ${sdpColor}1a, transparent 90%)`,
        }}
      >
        <div
          className="mb-1 font-mono text-[10.5px] font-bold uppercase tracking-[0.1em]"
          style={{ color: sdpColor }}
        >
          AI pay-off
        </div>
        <div className="text-[13.5px] leading-[1.6] text-[var(--tcc-text)]">
          {scenario.aiPayoff}
        </div>
      </div>
    </PanelSurface.Card>
  );
});
